import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import type {
  MyNotificationSettings,
  NotificationSettingsType,
} from "@/services/notificationSettingsService";
import { restError } from "@/models/client/RestResponse";

/**
 * The current user's notification subscriptions (LAR-27 follow-up). Backed by
 * the tenant's UsersNotificationSettings recipient lists via
 * GET/PUT/DELETE me/notifications/settings. Subscribing to PiiOverride is
 * additionally permission-gated server-side (ManagePiiSettings).
 */
const useNotificationSettings = () => {
  const queryClient = useQueryClient();
  const service = useItemService((services) => services.notificationSettings);

  const useQuerySettings = () =>
    useQuery<MyNotificationSettings>({
      queryKey: [PATH_KEYS.notificationSettings],
      queryFn: async () => {
        const response = await service.getMine();
        if (response.success) return response.value;
        throw restError(response);
      },
      staleTime: 60_000,
      retry: false,
    });

  const setSubscription = useMutation<
    unknown,
    Error,
    { settingsType: NotificationSettingsType; subscribed: boolean }
  >({
    mutationKey: [PATH_KEYS.notificationSettings, "set"],
    mutationFn: async ({ settingsType, subscribed }) => {
      const response = subscribed
        ? await service.subscribe(settingsType)
        : await service.unsubscribe(settingsType);
      if (response.success) return response.value;
      throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.notificationSettings],
      });
    },
  });

  return { useQuerySettings, setSubscription };
};

export default useNotificationSettings;
